import React from "react";
import { toast } from "react-toastify";

import { Table } from "src/core/dashboard/components/widgets";
import { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { ActionButton } from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import {
  ICallOff,
  useCallOffApi
} from "src/modules/call-off/api/call-off.api";
import { getCallOffColumns } from "src/modules/call-off/components/callOffTableColumns";
import { PageProps } from "src/pages/Router/routes";

const defaultPagination: PaginatedParams<ICallOff> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

const OrderCallOffsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history, terminology } = props;
  const [ listCallOffRes, listCallOff ] = useCallOffApi("LIST_CALL_OFFS");
  const [ deleteCallOffRes, deleteCallOffReq ] = useCallOffApi("DELETE_CALL_OFF");
  const TableRef = React.useRef<TableRefProps>(null);

  const createCallOff = React.useCallback(() => {
    history.push("/admin/orders/call-offs/create-edit");
  }, [ history ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<ICallOff>, pagination: PaginatedParams<ICallOff>}) => {
      listCallOff({
        pagination: params.pagination,
        filters: { ...params.filters }
      });
    },
    [ listCallOff ]
  );

  React.useEffect(() => {
    if (deleteCallOffRes.error) {
      toast.error(deleteCallOffRes.error.message);

      return;
    }

    if (deleteCallOffRes.complete) {
      toast.success("Deleted Call Off");
      TableRef.current?.reloadTable();
    }
  }, [ deleteCallOffRes ]);

  const deleteCallOff = React.useCallback((id: number) => {
    deleteCallOffReq({ callOffId: id });
  }, [ deleteCallOffReq ]);

  const columns = React.useMemo(() => {
    return getCallOffColumns({
      actionButton: info => {
        return (
          <ActionButton
            key={`action-${info.row.original.id}`}
            title="Actions"
            items={[
              {
                action: () => history.push(`/admin/orders/call-offs/orders/${info.row.original.id}`),
                title: "View Related Orders"
              },
              {
                action: () => history.push(`/admin/orders/call-offs/create-edit/${info.row.original.id}`),
                title: "View / Edit"
              },
              {
                action: () => deleteCallOff(info.row.original.id),
                title: "Delete",
                disabled: info.row.original.deleted
              }
            ]}
          />
        );
      },
      terminology

    });
  }, [
    terminology,
    history,
    deleteCallOff
  ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <LikeToDo
          copy="Call-Off Actions"
          buttons={[
            <ActionButton
              key="create-calloff"
              title="Create Call-off"
              items={[
                {
                  action: createCallOff,
                  title: `Create ${terminology.callOff?.singular.text ?? "Call off"}`
                }
              ]}
            />
          ]}
        />

        <Table
          ref={TableRef}
          error={listCallOffRes.error}
          tableOf={terminology.callOff?.plural.text ?? "Call offs"}
          loading={listCallOffRes.pending}
          data={listCallOffRes.data ? listCallOffRes.data.result : []}
          onFetchData={onFetchData}
          rowCount={listCallOffRes.data?.count ?? 0}
          pageCount={listCallOffRes.data?.pages ?? 0}
          defaultPagination={defaultPagination}
          columns={columns}
        />

      </MainContentWrapper>
    </Layout>

  );
};

export default OrderCallOffsPage;
